import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import HideIcon from '@/components/icons/HideIcon';
import ShowIcon from '@/components/icons/ShowIcon';
import InformationCircle from '@/components/icons/InformationCircle';

const PASSWORD_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

const generatePassword = (length = 12) => {
    const values = crypto.getRandomValues(new Uint32Array(length));
    return Array.from(values, (v) => PASSWORD_CHARS[v % PASSWORD_CHARS.length]).join('');
};

const SetPasswordSchema = z.object({
    newPassword: z
        .string({ message: 'main.superAdmin.users.form.password.error' })
        .min(8, { message: 'main.superAdmin.users.form.password.errorRules' }),
});

type SetPasswordFormData = z.infer<typeof SetPasswordSchema>;

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (newPassword: string) => Promise<void>;
    isSubmitting?: boolean;
};

const UserSetPasswordDialog: FC<Props> = ({ open, onOpenChange, onSubmit, isSubmitting }) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

    const { control, handleSubmit, reset, setValue, watch } = useForm<SetPasswordFormData>({
        resolver: zodResolver(SetPasswordSchema),
        defaultValues: { newPassword: '' },
    });

    const passwordValue = watch('newPassword');

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            reset({ newPassword: '' });
            setIsVisible(false);
        }
        onOpenChange(nextOpen);
    };

    const handleGenerateClick = () => {
        if (passwordValue) {
            setGenerateDialogOpen(true);
            return;
        }

        setValue('newPassword', generatePassword(), { shouldDirty: true, shouldValidate: true });
        setIsVisible(true);
    };

    const handleGenerateConfirm = () => {
        setValue('newPassword', generatePassword(), { shouldDirty: true, shouldValidate: true });
        setIsVisible(true);
        setGenerateDialogOpen(false);
    };

    const handleFormSubmit = handleSubmit(async (form) => {
        await onSubmit(form.newPassword);
        reset({ newPassword: '' });
        setIsVisible(false);
        onOpenChange(false);
    });

    return (
        <>
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {t('main.superAdmin.users.components.details.actions.setPassword.title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('main.superAdmin.users.components.details.actions.setPassword.description')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                            <h4 className="text-sm font-medium">
                                {t('main.superAdmin.users.form.password.title')}
                            </h4>
                            <Button type="button" variant="outline" onClick={handleGenerateClick}>
                                {t('main.superAdmin.users.form.password.generate')}
                            </Button>
                        </div>
                        <Controller
                            name="newPassword"
                            control={control}
                            render={({ field: { value, onChange, onBlur }, fieldState }) => (
                                <>
                                    <div className="relative flex items-center">
                                        <input
                                            type={isVisible ? 'text' : 'password'}
                                            value={value ?? ''}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            placeholder={t(
                                                'main.superAdmin.users.form.password.placeholder',
                                            )}
                                            autoComplete="new-password"
                                            className={cn(
                                                'w-full p-3.5 pr-12 border border-popover-100 rounded-semi-xl text-xs text-popover-800 bg-popover-10 focus:outline-hidden focus:border focus:border-primary-color-1000 placeholder:text-popover-600 transition-colors duration-150',
                                                fieldState.invalid ? 'border-error-1000' : '',
                                            )}
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 flex size-6 items-center justify-center"
                                            onClick={() => setIsVisible((current) => !current)}
                                            aria-label={
                                                isVisible
                                                    ? t('main.superAdmin.users.form.password.hide')
                                                    : t('main.superAdmin.users.form.password.show')
                                            }>
                                            {isVisible ? <ShowIcon /> : <HideIcon />}
                                        </button>
                                    </div>
                                    {fieldState.invalid && (
                                        <div className="flex items-center gap-1.5">
                                            <InformationCircle className="size-4 stroke-error-1000" />
                                            <span className="text-xs text-error-1000">
                                                {t(fieldState.error?.message as string)}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}>
                            {t('main.superAdmin.users.components.details.actions.setPassword.cancel')}
                        </Button>
                        <Button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => void handleFormSubmit()}>
                            {t('main.superAdmin.users.components.details.actions.setPassword.submit')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {t('main.superAdmin.users.form.password.generateDialogTitle')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('main.superAdmin.users.form.password.generateDialogDescription')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setGenerateDialogOpen(false)}>
                            {t('main.superAdmin.users.form.password.generateCancel')}
                        </Button>
                        <Button type="button" onClick={handleGenerateConfirm}>
                            {t('main.superAdmin.users.form.password.generateConfirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default UserSetPasswordDialog;
